import { createContext, useContext, useEffect, useMemo, useState } from "react";
import useFetch from "../server/useFetch";
import query from "../server/constants.api";
import { isEmpty } from "../utils/hooks/isEmpty";
import { useAuthContext } from "./AuthContext";
import logger from "../server/logger";

const GlobalFilterContext = createContext(null);

export const useGlobalFilterContext = () => {
    const context = useContext(GlobalFilterContext);

    if (!context) {
        throw new Error("useGlobalFilterContext must be used within an ContentProvider");
    }
    return context;
};

export const GlobalFilterProvider = (props) => {
    const { user } = useAuthContext();
    const [filterContent, setFilterContent] = useState(null);//Filters for the map
    const [enableApplyButton, setenableApplyButton] = useState(false);
    const [dropdownEntities, setDropdownEntities] = useState([]);
    const [dropdownSubEntities, setDropdownSubEntities] = useState([]);
    const [entityToFetch, setEntityToFetch] = useState(null);
    const [subEntityToFetch, setSubEntityToFetch] = useState(null);
    const [entityCoordinates, setEntityCoordinates] = useState(null);
    const [isToApplyFilters, setIsToApplyFilters] = useState(false); //Triggers certain components to apply filters
    const [classesDropdown, setClassesDropdown] = useState([]);
    const [classes, setClasses] = useState(null);
    const [lockButtons, setLockButtons] = useState(true);

    const { data: entitiesData, refetch: refetchEntities } = useFetch(query.path.entinties, 'getEntitiesForDropdown', { tenant: user?.attributes?.['custom:tenant'] }, user?.attributes?.['custom:tenant'] ? true : false);
    const { data: classesData, refetch: typesRefetch, isFetching: typesFetching, error: classesDataError } = useFetch(query.path.dropdown_contents, "getTypes",
        {
            dropdown: 'Types',
            contentTofilter: subEntityToFetch?.id ?? entityToFetch?.id,
            // language: language
        },
        subEntityToFetch ? true : entityToFetch ? true : false
    );

    useEffect(() => {
        typesRefetch();
    }, [entityToFetch, subEntityToFetch])

    useEffect(() => {
        if (classesData?.data && !isEmpty(classesData?.data)) {
            // setClasses(classesData.data?.map(item => ({
            //     name: item.name,
            //     options: item.options.map(option => option.value)
            // })));
            setClassesDropdown(classesData.data);
        } else {
            setClassesDropdown([]);
        }
        setClasses([]);
    }, [classesData, classesDataError])

    useEffect(() => {
        try {
            if (entitiesData?.data && !isEmpty(entitiesData?.data)) {
                const indexData = entitiesData?.data?.filter(x => x.Entity.BU_Index > 0)?.map(x => {
                    const entityLabel = x.Entity.tenant.split('/');
                    const name = x.Entity.name.split('.')?.[0];

                    return {
                        id: x.Entity.id,
                        value: x.Entity.tenant,
                        label: entityLabel[entityLabel.length - 1],
                        name: name,
                        cordinates: { latitude: x.Entity.latitude, longitude: x.Entity.longitude },
                        BU_Index: x.Entity.BU_Index,
                        Subentity: x?.['Subentity'] ?? null,
                        zoom: x.Entity.Zoom
                    }
                })?.sort(function (a, b) {
                    if (a.BU_Index > b.BU_Index) {
                        return -1;
                    }
                    if (a.BU_Index < b.BU_Index) {
                        return 1;
                    }
                    return 0;
                });

                const alphabaticData = entitiesData?.data?.filter(x => x.Entity.BU_Index == 0)?.map(x => {
                    const entityLabel = x.Entity.tenant.split('/');
                    const name = x.Entity.name.split('.')?.[0];

                    return {
                        id: x.Entity.id,
                        value: x.Entity.tenant,
                        label: entityLabel[entityLabel.length - 1],
                        name: name,
                        cordinates: { latitude: x.Entity.latitude, longitude: x.Entity.longitude },
                        BU_Index: x.Entity.BU_Index,
                        Subentity: x?.['Subentity'] ?? null,
                        zoom: x.Entity.Zoom
                    }
                })?.sort(function (a, b) {
                    if (a.label < b.label) {
                        return -1;
                    }
                    if (a.label > b.label) {
                        return 1;
                    }
                    return 0;
                });

                const data = indexData.concat(alphabaticData);

                setDropdownEntities(data);
                if (data?.[0]) {
                    setEntityToFetch(data[0]);
                    setEntityCoordinates({ latitude: data[0]?.cordinates?.latitude, longitude: data[0]?.cordinates?.longitude });
                }

                const subEntitiesData = data?.[0]?.Subentity?.map(y => {
                    const subEntityLabel = y?.tenant?.split('/');
                    return {
                        id: y.id,
                        value: y.tenant,
                        label: subEntityLabel[subEntityLabel.length - 1],
                        cordinates: { latitude: y.latitude, longitude: y.longitude },
                        BU_Index: y.BU_Index
                    }
                })?.sort(function (a, b) {
                    if (a.BU_Index > b.BU_Index) {
                        return -1;
                    }
                    if (a.BU_Index < b.BU_Index) {
                        return 1;
                    }
                    return 0;
                }) ?? [];

                setDropdownSubEntities(subEntitiesData);
                if (subEntitiesData?.[0] && subEntityToFetch == null) {
                    setSubEntityToFetch(subEntitiesData[0]);
                }
            }
        } catch (error) {
            // logger.error('Error setting Entities and Subentities')
            console.error('(Homepage page) Error in useEffect setting Entities and Subentities:', error);
            //reportError(error); // Send error report to backend
        }
    }, [entitiesData])

    async function handleClassesChange(family, newOptions) {
        setClasses(prevState => {
            if (isEmpty(prevState)) {
                return [{ name: family, options: newOptions }];
            }
            const updatedState = prevState.map(item => {
                if (item.name.toLowerCase() === family.toLowerCase()) {
                    return { ...item, options: newOptions };
                }
                return item;
            });
            const familyExists = updatedState.some(item => item.name.toLowerCase() === family.toLowerCase());
            if (!familyExists) {
                updatedState.push({ name: family, options: newOptions });
            }
            return updatedState;
        });
    };

    function onApplyFilters() {
        const filterContent = {
            "id": subEntityToFetch?.id ?? entityToFetch?.id,
            "classes": classes.map(x => x.options).flat() ?? [],
            "municipalityCoordenates": subEntityToFetch?.cordinates ?? entityToFetch?.cordinates,
            "tenant": subEntityToFetch?.value ?? entityToFetch?.value,
            "name": subEntityToFetch?.label ?? entityToFetch?.label
        }

        setFilterContent(filterContent);
        setIsToApplyFilters(true);
    }

    const value = useMemo(() => {

        const handleEntitiesOptionsChange = (record) => {
            try {
                if (record) {
                    // const subEntitiesTenants = entitiesData?.data?.filter(x => x.Entity.tenant === value);
                    const subEntitiesData = record?.['Subentity']?.map(y => {
                        const subEntityLabel = y?.tenant?.split('/');
                        return {
                            id: y.id,
                            value: y.tenant,
                            label: subEntityLabel[subEntityLabel.length - 1],
                            cordinates: { latitude: y.latitude, longitude: y.longitude },
                            BU_Index: y.BU_Index
                        }
                    })?.sort(function (a, b) {
                        if (a.BU_Index > b.BU_Index) {
                            return -1;
                        }
                        if (a.BU_Index < b.BU_Index) {
                            return 1;
                        }
                        return 0;
                    }) ?? [];
                    setDropdownSubEntities(subEntitiesData);
                    setSubEntityToFetch(isEmpty(subEntitiesData) ? null : subEntitiesData[0]);

                    const entity = dropdownEntities.filter(x => x.value === record.value);
                    setEntityToFetch(entity[0]);//Converted to array because its what the API receives as a param
                    setEntityCoordinates({ latitude: entity[0]?.cordinates?.latitude, longitude: entity[0]?.cordinates?.longitude });
                    // setIsToFetchEmissionsBySector(true);
                }
            } catch (error) {
                // logger.error('Error in handleEntitiesOptionsChange function');
                console.error('(Homepage page) Error in handleEntitiesOptionsChange function:', error);
                //reportError(error); // Send error report to backend
            }
        };

        const handleSubEntitiesOptionsChange = (record) => {
            if (record) {
                setSubEntityToFetch(record);
            }
        }

        return {
            filterContent,
            enableApplyButton,
            setenableApplyButton,
            dropdownEntities,
            dropdownSubEntities,
            handleEntitiesOptionsChange,
            entityToFetch,
            subEntityToFetch,
            handleSubEntitiesOptionsChange,
            entityCoordinates,
            isToApplyFilters,
            setIsToApplyFilters,
            classes,
            classesDropdown,
            typesFetching,
            handleClassesChange,
            onApplyFilters
        };
    }, [filterContent, enableApplyButton, dropdownEntities, dropdownSubEntities, entityToFetch, subEntityToFetch, isToApplyFilters, classes, classesDropdown, typesFetching]);

    return <GlobalFilterContext.Provider value={value}>{props.children}</GlobalFilterContext.Provider>
}